<template>
  <div class="bind-check-lists">
    <div class="drawer-header">
      <div>
        <h5 class="title">Привязать чек-листы</h5>
        <p class="subtitle">Выберите чек-листы для привязки к конструктиву</p>
      </div>
      <q-btn flat round dense icon="close" @click="$emit('close')" class="close-btn" />
    </div>

    <div class="content">
      <div class="form-section">
        <p class="form-section-title">Конструктив:</p>
        <p class="form-section-value">{{ this.workSet.work_set_name }}</p>
      </div>

      <q-linear-progress v-if="this.isLoading" indeterminate color="primary" class="q-mb-md" />

      <div v-if="this.workSet && this.bindedCheckLists" class="check-lists-section">
        <div class="section-header">
          <h6 class="section-title">
            Привязанные чек-листы
            <q-badge v-if="this.bindedCheckLists.check_lists" color="primary"
              :label="this.bindedCheckLists.check_lists.length" outline />
          </h6>

          <CustomButton v-if="!this.isEditMode" color="primary-light" @click="enableEditMode" outlined
            style="width: auto; height: 32px;">
            Редактировать
          </CustomButton>

          <div v-else class="edit-actions">
            <CustomButton color="negative" @click="this.cancelEdit" style="width: auto; height: 32px;">
              Отмена
            </CustomButton>
            <CustomButton color="primary-light" @click="this.saveChanges" :loading="isSaving"
              style="width: auto; height: 32px;">
              Сохранить
            </CustomButton>
          </div>
        </div>

        <div v-if="this.bindedCheckLists.check_lists && this.bindedCheckLists.check_lists.length > 0"
          class="check-lists-list">
          <q-list bordered separator>
            <q-item v-for="checkList in this.bindedCheckLists.check_lists" :key="checkList.work_set_check_id"
              class="check-list-item">
              <q-item-section v-if="this.isEditMode" side>
                <q-checkbox v-model="checkList.selected" color="primary" />
              </q-item-section>

              <q-item-section side>
                <q-icon name="check_circle" color="positive" size="24px" />
              </q-item-section>

              <q-item-section>
                <q-item-label class="check-list-name">{{ checkList.check_name }}</q-item-label>
                <q-item-label caption v-if="checkList.norm">
                  <q-chip dense size="sm" outline color="grey-7">
                    Норма: {{ checkList.norm }}
                  </q-chip>
                </q-item-label>
              </q-item-section>

              <q-item-section side v-if="checkList.is_required">
                <q-badge color="warning" label="Обязательный" />
              </q-item-section>
            </q-item>
          </q-list>
        </div>

        <div v-else class="empty-state">
          <q-icon name="checklist" size="64px" color="grey-5" />
          <p class="empty-text">Нет привязанных чек-листов</p>
        </div>

        <div v-if="this.isEditMode" class="add-check-list-section">
          <div class="add-check-list-row">
            <CustomSelect :options="availableCheckLists" label="Чек-лист" option-label-key="check_name"
              option-value-key="check_list_id" :value="selectedCheckListToAdd?.check_list_id"
              @change="onCheckListSelectChange" placeholder="Выберите чек-лист для добавления" class="select-check-list"
              style="width: 70%;" />

            <CustomButton color="positive" style="width: auto; height: 32px;" @click="addCheckList"
              :disable="!selectedCheckListToAdd">
              Добавить
              <q-icon name="add" size="16px" />
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getWorkSetChecks,
  createWorkSetChecks,
} from "@/views/Constructives/services";
import { getCheckListsList } from "@/views/Constructives/services";
import CustomButton from '@/components/common/CustomButton.vue';
import CustomSelect from '@/components/form/CustomSelect';

export default {
  name: 'BindCheckListsDrawer',
  components: {
    CustomButton,
    CustomSelect
  },
  props: {
    workSet: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isEditMode: false,
      bindedCheckLists: null,
      originalBindedCheckLists: null,
      allCheckLists: [],
      selectedCheckListToAdd: null,
    };
  },
  computed: {
    availableCheckLists() {
      if (!this.bindedCheckLists?.check_lists || !this.allCheckLists) {
        return this.allCheckLists;
      }
      const bindedIds = this.bindedCheckLists.check_lists.map(cl => cl.check_list_id);
      return this.allCheckLists.filter(cl => !bindedIds.includes(cl.check_list_id));
    }
  },
  methods: {
    async loadAllCheckLists() {
      const res = await getCheckListsList();
      if (res?.data) {
        this.allCheckLists = res.data;
      }
    },

    onCheckListSelectChange(value) {
      this.selectedCheckListToAdd = this.availableCheckLists.find(cl => cl.check_list_id === value);
    },

    async loadBindedCheckLists() {
      if (!this.workSet) return;

      this.isLoading = true;
      const data = await getWorkSetChecks(this.workSet.work_set_id);

      if (data && data.length > 0) {
        this.bindedCheckLists = data[0];
        if (this.bindedCheckLists.check_lists) {
          this.bindedCheckLists.check_lists = this.bindedCheckLists.check_lists.map(cl => ({
            ...cl,
            selected: true
          }));
        }
      } else {
        this.bindedCheckLists = {
          work_set_id: this.workSet.work_set_id,
          work_set_name: this.workSet.work_set_name,
          check_lists: []
        };
      }

      this.isLoading = false;
    },

    enableEditMode() {
      this.isEditMode = true;
      this.originalBindedCheckLists = JSON.parse(JSON.stringify(this.bindedCheckLists));
    },

    cancelEdit() {
      this.bindedCheckLists = JSON.parse(JSON.stringify(this.originalBindedCheckLists));
      this.isEditMode = false;
      this.selectedCheckListToAdd = null;
    },

    addCheckList() {
      if (!this.selectedCheckListToAdd) return;

      this.bindedCheckLists.check_lists.push({
        check_list_id: this.selectedCheckListToAdd.check_list_id,
        check_name: this.selectedCheckListToAdd.check_name,
        norm: this.selectedCheckListToAdd.norm,
        is_required: this.selectedCheckListToAdd.is_required,
        selected: true,
        isNew: true
      });

      this.selectedCheckListToAdd = null;
    },

    async saveChanges() {
      if (!this.workSet) return;

      this.isSaving = true;
      const checkListIds = this.bindedCheckLists.check_lists
        .filter(cl => cl.selected)
        .map(cl => cl.check_list_id);

      await createWorkSetChecks(
        this.workSet.work_set_id,
        checkListIds
      );

      await this.loadBindedCheckLists();
      this.isEditMode = false;
      this.isSaving = false;
    }
  },
  async mounted() {
    await Promise.all([
      this.loadAllCheckLists()
    ]);

    await this.loadBindedCheckLists();
  }
};
</script>

<style scoped lang="scss">
.bind-check-lists {
  height: 100%;
  display: flex;
  flex-direction: column;

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px 24px 16px 24px;
    border-bottom: 1px solid #e0e0e0;

    .title {
      margin: 0 0 4px 0;
      font-size: 18px;
      font-weight: 600;
      color: #1a1a1a;
    }

    .subtitle {
      margin: 0;
      font-size: 13px;
      color: #666;
    }

    .close-btn {
      color: #666;

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  .content {
    padding: 24px;
    flex: 1;
    overflow-y: auto;
  }

  .form-section {
    margin-bottom: 24px;
    padding: 16px;
    background: #fafafa;
    border-radius: 6px;
    border: 1px solid #e0e0e0;

    .form-section-title {
      font-size: 18px;
      font-weight: 600;
      color: #262626;
      margin-bottom: 4px;
    }

    .form-section-value {
      font-size: 16px;
      font-weight: 400;
      color: #262626;
    }
  }

  .check-lists-section {
    margin-top: 24px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .section-title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #262626;
      }

      .edit-btn {
        font-size: 13px;
        text-transform: none;
      }

      .edit-actions {
        display: flex;
        gap: 8px;

        .q-btn {
          font-size: 13px;
          text-transform: none;
        }
      }
    }

    .check-lists-list {
      margin-bottom: 16px;

      .check-list-item {
        transition: background-color 0.2s;

        &:hover {
          background-color: #f5f5f5;
        }

        .check-list-name {
          font-size: 14px;
          font-weight: 500;
          color: #1a1a1a;
        }
      }
    }

    .add-check-list-section {
      margin-top: 20px;
      padding: 16px;
      background: #fafafa;
      border-radius: 6px;
      border: 1px solid #e0e0e0;

      .add-check-list-row {
        display: flex;
        gap: 10px;
        align-items: end;

        .select-check-list {
          flex: 1;
        }
      }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;

    .q-icon {
      opacity: 0.4;
      margin-bottom: 12px;
    }

    .empty-text {
      margin: 0;
      font-size: 14px;
      color: #999;
      font-weight: 400;
    }
  }
}
</style>
